/**
 * Desktop app updates (Tauri updater plugin, docs/RELEASES.md).
 *
 * A few seconds after start the installed app asks the releases feed (tauri.conf.json → plugins.updater.endpoints)
 * whether there is a newer version. If so, "New update available" offers:
 *  - Install now           → download (signature checked against plugins.updater.pubkey), install, restart.
 *                            Windows: the installer closes the app, updates it and opens it again by itself.
 *  - Install on next launch → remembered here; the next start installs it right away, without asking again.
 *  - Cancel                → nothing happens; the next start asks again.
 * Help (?) → "Check for updates" asks on demand. Browsers and `pnpm desktop:dev` never check.
 */
import { create } from "zustand";
import { isDesktop, openExternal } from "./desktop.ts";
import type { Update } from "@tauri-apps/plugin-updater";

/** where people can download the newest version by hand (if an update can't install itself) */
export const RELEASES_PAGE = "https://github.com/lab-43/mc-planner-releases/releases/latest";
const NEXT_LAUNCH_KEY = "mcp-update-next-launch";
/** first automatic check after start — the world/wizard gets the first seconds */
const FIRST_CHECK_MS = 4000;

export interface UpdateInfo {
  version: string;
  /** the running version */
  current: string;
  /** release notes (the part above the "---" line of the GitHub release text) */
  notes: string;
  date?: string;
}

export type UpdatePhase =
  | "idle"
  | "checking" // only shown for a manual check
  | "available" // the dialog with the three choices
  | "downloading"
  | "installing"
  | "uptodate" // manual check found nothing
  | "error";

interface UpdaterState {
  /** version of this app (desktop only) */
  current: string | null;
  info: UpdateInfo | null;
  phase: UpdatePhase;
  /** the dialog is open */
  open: boolean;
  /** installing because the person chose "Install on next launch" last time */
  queued: boolean;
  /** 0…1 while downloading, null = size unknown */
  progress: number | null;
  /** bytes downloaded / total (0 = unknown) */
  bytes: [number, number];
  error: string | null;
  /** what went wrong: asking the feed, or downloading/installing */
  failed: "check" | "install" | null;
  set: (p: Partial<UpdaterState>) => void;
}

export const useUpdater = create<UpdaterState>((set) => ({
  current: null,
  info: null,
  phase: "idle",
  open: false,
  queued: false,
  progress: null,
  bytes: [0, 0],
  error: null,
  failed: null,
  set: (p) => set(p),
}));

/** the plugin's handle of the found update (a Rust resource; closed when replaced) */
let found: Update | null = null;

const store = {
  get: (): string | null => { try { return localStorage.getItem(NEXT_LAUNCH_KEY); } catch { return null; } },
  set: (v: string) => { try { localStorage.setItem(NEXT_LAUNCH_KEY, v); } catch { /* ignore */ } },
  clear: () => { try { localStorage.removeItem(NEXT_LAUNCH_KEY); } catch { /* ignore */ } },
};

/** Version the person chose to install "on next launch" (or null). */
export const queuedVersion = () => store.get();

/** Release notes without the download instructions below the first "---" line (GitHub release text). */
export function releaseNotes(body: string | undefined): string {
  if (!body) return "";
  const cut = body.split(/\r?\n/).findIndex((l) => /^\s*(---+|\*\*\*+|___+)\s*$/.test(l));
  return (cut >= 0 ? body.split(/\r?\n/).slice(0, cut).join("\n") : body).trim().slice(0, 4000);
}

const message = (e: unknown) => {
  const m = String((e as Error)?.message ?? e ?? "unknown error");
  if (/signed for version|tampered/i.test(m)) return "The download doesn't match the version the update server announced, so it wasn't installed.";
  if (/signature/i.test(m)) return "The download didn't pass the signature check, so it wasn't installed.";
  if (/network|connect|dns|timed? ?out|error sending request|offline/i.test(m)) return "Couldn't reach the update server — are you online?";
  return m;
};

/** Called once at start (App). Does nothing in the browser and in `pnpm desktop:dev`. */
export async function initUpdater() {
  if (!isDesktop()) return;
  try {
    const { getVersion } = await import("@tauri-apps/api/app");
    useUpdater.getState().set({ current: await getVersion() });
  } catch { /* old shell without the app API */ }
  if (import.meta.env.DEV) return; // a dev build would offer to "update" itself to the released installer
  // chosen "Install on next launch" last time: install straight away; otherwise look a few seconds after start
  if (queuedVersion()) await checkForUpdates({ queued: true });
  else setTimeout(() => void checkForUpdates(), FIRST_CHECK_MS);
}

/**
 * Ask the releases feed. Automatic checks stay silent unless there is an update (offline, no release yet, …);
 * a manual check (Help → Check for updates) always shows the result. `install`: install right away if found (retry).
 */
export async function checkForUpdates(opts: { manual?: boolean; queued?: boolean; install?: boolean } = {}) {
  const st = useUpdater.getState();
  if (!isDesktop() || st.phase === "checking" || st.phase === "downloading" || st.phase === "installing") return;
  if (opts.manual) st.set({ open: true, phase: "checking", error: null, failed: null });
  try {
    const { check } = await import("@tauri-apps/plugin-updater");
    const u = await check({ timeout: 20_000 });
    void found?.close().catch(() => {});
    found = u;
    if (!u) {
      store.clear(); // whatever was queued is installed (or was withdrawn)
      useUpdater.getState().set(opts.manual ? { phase: "uptodate", info: null } : { phase: "idle", info: null });
      return;
    }
    const info: UpdateInfo = { version: u.version, current: u.currentVersion, notes: releaseNotes(u.body), date: u.date };
    if (opts.queued || opts.install) {
      useUpdater.getState().set({ info, open: true, queued: !!opts.queued });
      await installNow();
      return;
    }
    useUpdater.getState().set({ info, phase: "available", open: true, queued: false, error: null });
  } catch (e) {
    console.warn("update check failed", e);
    if (opts.manual) useUpdater.getState().set({ phase: "error", error: message(e), failed: "check" });
    else useUpdater.getState().set({ phase: "idle" });
  }
}

/** Download, install, restart. Windows: the installer closes the app and starts the new version itself. */
export async function installNow() {
  if (!found) return;
  const st = useUpdater.getState();
  st.set({ phase: "downloading", progress: 0, bytes: [0, 0], error: null, failed: null, open: true });
  let total = 0;
  let got = 0;
  let shown = -1;
  try {
    await found.downloadAndInstall((ev) => {
      if (ev.event === "Started") {
        total = ev.data.contentLength ?? 0;
        useUpdater.getState().set({ progress: total ? 0 : null, bytes: [0, total] });
      } else if (ev.event === "Progress") {
        got += ev.data.chunkLength;
        const pct = total ? Math.floor((got / total) * 100) : -1;
        if (pct !== shown || !total) { shown = pct; useUpdater.getState().set({ progress: total ? Math.min(1, got / total) : null, bytes: [got, total] }); }
      } else if (ev.event === "Finished") {
        useUpdater.getState().set({ phase: "installing", progress: 1 });
      }
    });
    // (Windows never gets here — the app was closed for the installer)
    store.clear();
    const { relaunch } = await import("@tauri-apps/plugin-process");
    await relaunch();
  } catch (e) {
    console.warn("update failed", e);
    store.clear(); // don't retry a broken update at every start; the dialog offers the manual download
    useUpdater.getState().set({ phase: "error", error: message(e), failed: "install" });
  }
}

/** Remember the choice; the next start installs it without asking. */
export function installOnNextLaunch() {
  const info = useUpdater.getState().info;
  if (info) store.set(info.version);
  useUpdater.getState().set({ open: false, phase: "idle" });
  return info?.version ?? null;
}

/** Cancel / Close: ask again at the next start. */
export function closeUpdateDialog() {
  const st = useUpdater.getState();
  if (st.phase === "downloading" || st.phase === "installing") return;
  st.set({ open: false, phase: "idle", error: null, failed: null, queued: false });
}

export function openReleasesPage() {
  void openExternal(RELEASES_PAGE);
}
